// @ts-nocheck
import { useState, useCallback } from 'react';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import {
  Stack,
  Paper,
  Group,
  Text,
  Button,
  Modal,
  Textarea,
  Badge,
  ActionIcon,
  SimpleGrid,
  Tabs,
  ThemeIcon,
  Loader,
  Center,
  Divider,
} from '@mantine/core';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { notifications } from '@mantine/notifications';
import {
  IconMapPin,
  IconShield,
  IconAlertTriangle,
  IconCheck,
  IconX,
  IconArrowLeft,
  IconClock,
  IconUser,
} from '@tabler/icons-react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import { StatusBadge } from '../../components/StatusBadge';
import {
  useGeoFence,
  useGeoFenceViolations,
  useReviewViolation,
} from '../../hooks/useGeoFencing';
import type { GeoFenceViolation } from '../../types/geofencing';

// ── Constants ───────────────────────────────────────────────────────

const ENFORCEMENT_COLORS: Record<string, string> = {
  STRICT: 'red',
  WARNING: 'orange',
  LOG_ONLY: 'gray',
};

const ENFORCEMENT_LABELS: Record<string, string> = {
  STRICT: 'Strict - Blocks punch outside fence',
  WARNING: 'Warning - Allows punch with manager alert',
  LOG_ONLY: 'Log Only - Silent tracking for analytics',
};

const VIOLATION_STATUS_COLORS: Record<string, string> = {
  PENDING_REVIEW: 'yellow',
  APPROVED: 'green',
  REJECTED: 'red',
};

// ── Component ───────────────────────────────────────────────────────

export default function GeoFenceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [violationsPage, setViolationsPage] = useState(1);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [reviewTarget, setReviewTarget] = useState<GeoFenceViolation | null>(null);
  const [reviewAction, setReviewAction] = useState<'APPROVED' | 'REJECTED'>('APPROVED');
  const [reviewNote, setReviewNote] = useState('');

  const { data: fence, loading: fenceLoading } = useGeoFence(id ?? '');
  const { data: rawViolations, loading: violationsLoading } = useGeoFenceViolations({
    geoFenceId: id,
    page: violationsPage,
    perPage: 15,
  });
  const violations = rawViolations as any;
  const reviewMutation = useReviewViolation();

  const handleOpenReview = useCallback(
    (violation: GeoFenceViolation, action: 'APPROVED' | 'REJECTED') => {
      setReviewTarget(violation);
      setReviewAction(action);
      setReviewNote('');
      setReviewModalOpen(true);
    },
    [],
  );

  const handleConfirmReview = useCallback(() => {
    if (!reviewTarget) return;
    reviewMutation.mutate(
      {
        id: reviewTarget.id,
        status: reviewAction,
        reviewNote: reviewNote.trim() || undefined,
      },
      {
        onSuccess: () => {
          notifications.show({
            title: reviewAction === 'APPROVED' ? 'Violation Approved' : 'Violation Rejected',
            message: `Violation for ${reviewTarget.employeeName} has been ${reviewAction.toLowerCase()}.`,
            color: reviewAction === 'APPROVED' ? 'green' : 'red',
          });
          setReviewModalOpen(false);
          setReviewTarget(null);
        },
        onError: (error: any) => {
          notifications.show({
            title: 'Review Failed',
            message: error instanceof Error ? error.message : 'An error occurred.',
            color: 'red',
          });
        },
      },
    );
  }, [reviewTarget, reviewAction, reviewNote, reviewMutation]);

  if (fenceLoading) {
    return (
      <Center h="50vh">
        <Loader size="lg" />
      </Center>
    );
  }

  if (!fence) {
    return (
      <Center h="50vh">
        <Stack ta="center" gap="sm">
          <IconMapPin size={48} stroke={1.5} color="var(--mantine-color-dimmed)" />
          <Text c="dimmed">GeoFence not found</Text>
          <Button
            variant="light"
            icon={<IconArrowLeft size={16} />}
            onClick={() => navigate('/admin/workforce/geo-fences')}
          >
            Back to GeoFences
          </Button>
        </Stack>
      </Center>
    );
  }

  const violationColumns: Column<GeoFenceViolation>[] = [
    {
      key: 'employeeName',
      label: 'Employee',
      sortable: true,
      render: (row) => (
        <Group gap="xs">
          <IconUser size={14} />
          <Text size="sm">{row.employeeName}</Text>
        </Group>
      ),
    },
    {
      key: 'action',
      label: 'Action',
      width: 120,
      render: (row) => (
        <Badge variant="light" color="blue" size="sm">
          {row.action.replace(/_/g, ' ')}
        </Badge>
      ),
    },
    {
      key: 'distanceFromFence',
      label: 'Distance',
      width: 110,
      render: (row) => (
        <Text size="sm" fw={500} c="red">
          {row.distanceFromFence >= 1000
            ? `${(row.distanceFromFence / 1000).toFixed(1)} km`
            : `${Math.round(row.distanceFromFence)} m`}
        </Text>
      ),
    },
    {
      key: 'coordinates',
      label: 'Coordinates',
      width: 180,
      render: (row) => (
        <Text size="xs" c="dimmed" ff="monospace">
          {row.latitude.toFixed(5)}, {row.longitude.toFixed(5)}
        </Text>
      ),
    },
    {
      key: 'timestamp',
      label: 'Timestamp',
      width: 170,
      sortable: true,
      render: (row) => (
        <Group gap={4}>
          <IconClock size={12} />
          <Text size="sm">{new Date(row.timestamp).toLocaleString()}</Text>
        </Group>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      width: 140,
      render: (row) => (
        <StatusBadge status={row.status} colorMap={VIOLATION_STATUS_COLORS} />
      ),
    },
  ];

  return (
    <Stack gap="md">
      <PageHeader
        title={fence.name}
        subtitle={fence.description || undefined}
        breadcrumbs={[
          { label: 'Settings', to: '/admin/workforce/settings' },
          { label: 'GEO Fencing', to: '/admin/workforce/geo-fences' },
          { label: fence.name },
        ]}
        actions={
          <Button
            variant="default"
            icon={<IconArrowLeft size={16} />}
            onClick={() => navigate('/admin/workforce/geo-fences')}
          >
            Back
          </Button>
        }
      />

      {/* Map placeholder + Summary */}
      <SimpleGrid cols={{ base: 1, md: 2 }}>
        <Paper withBorder p="lg" radius="md">
          <Stack ta="center" justify="center" h={260} gap="sm">
            <ThemeIcon size={64} radius="xl" variant="light" color="blue">
              <IconMapPin size={32} />
            </ThemeIcon>
            <Text fw={600} size="lg">
              {fence.type === 'CIRCLE' ? 'Circle Fence' : 'Polygon Fence'}
            </Text>
            <Text size="sm" c="dimmed" ta="center">
              Center: {fence.latitude.toFixed(6)}, {fence.longitude.toFixed(6)}
            </Text>
            {fence.type === 'CIRCLE' && fence.radius && (
              <Text size="sm" c="dimmed">
                Radius:{' '}
                {fence.radius >= 1000
                  ? `${(fence.radius / 1000).toFixed(1)} km`
                  : `${fence.radius} m`}
              </Text>
            )}
            {fence.type === 'POLYGON' && fence.polygon && (
              <Text size="sm" c="dimmed">
                {fence.polygon.length} vertices
              </Text>
            )}
            {fence.address && (
              <Text size="xs" c="dimmed" ta="center" maw={300}>
                {fence.address}
              </Text>
            )}
          </Stack>
        </Paper>

        <Paper withBorder p="lg" radius="md">
          <Stack gap="md">
            <Text fw={600} size="lg">
              GeoFence Details
            </Text>
            <Divider />

            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                Enforcement Mode
              </Text>
              <Badge
                variant="light"
                color={ENFORCEMENT_COLORS[fence.enforcementMode] ?? 'gray'}
                icon={<IconShield size={12} />}
              >
                {fence.enforcementMode}
              </Badge>
            </Group>
            <Text size="xs" c="dimmed" mt={-8}>
              {ENFORCEMENT_LABELS[fence.enforcementMode] ?? ''}
            </Text>

            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                Type
              </Text>
              <Badge variant="light" color={fence.type === 'CIRCLE' ? 'blue' : 'violet'}>
                {fence.type}
              </Badge>
            </Group>

            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                Branch
              </Text>
              <Text size="sm" fw={500}>
                {fence.branchName || fence.branchId || 'Not assigned'}
              </Text>
            </Group>

            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                Employees
              </Text>
              <Text size="sm" fw={500}>
                {fence.employeeCount ?? 0}
              </Text>
            </Group>

            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                Allowed Actions
              </Text>
              <Group gap={4}>
                {fence.allowedActions.map((action: string) => (
                  <Badge key={action} variant="outline" size="xs">
                    {action.replace(/_/g, ' ')}
                  </Badge>
                ))}
              </Group>
            </Group>

            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                Version
              </Text>
              <Badge variant="light" color="gray">
                v{fence.version ?? 1}
              </Badge>
            </Group>

            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                Status
              </Text>
              <StatusBadge status={fence.isActive === false ? 'inactive' : 'active'} />
            </Group>

            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                Created
              </Text>
              <Text size="sm">
                {new Date(fence.createdAt).toLocaleDateString()}
              </Text>
            </Group>
          </Stack>
        </Paper>
      </SimpleGrid>

      {/* Tabs: Violations */}
      <Tabs defaultValue="violations">
        <Tabs.List>
          <Tabs.Tab
            value="violations"
            icon={<IconAlertTriangle size={16} />}
          >
            Violations
            {violations?.geoFenceViolations && violations.geoFenceViolations.summary.pendingCount > 0 && (
              <Badge size="xs" color="red" variant="filled" ml={6}>
                {violations.geoFenceViolations.summary.pendingCount}
              </Badge>
            )}
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="violations" pt="md">
          <Stack gap="md">
            {/* Violation summary stats */}
            {violations?.geoFenceViolations && (
              <SimpleGrid cols={{ base: 2, sm: 4 }}>
                <Paper withBorder p="sm" radius="md">
                  <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                    Total
                  </Text>
                  <Text fw={700} fz="xl">
                    {violations.geoFenceViolations.summary.totalViolations}
                  </Text>
                </Paper>
                <Paper withBorder p="sm" radius="md">
                  <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                    Pending
                  </Text>
                  <Text fw={700} fz="xl" c="yellow">
                    {violations.geoFenceViolations.summary.pendingCount}
                  </Text>
                </Paper>
                <Paper withBorder p="sm" radius="md">
                  <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                    Approved
                  </Text>
                  <Text fw={700} fz="xl" c="green">
                    {violations.geoFenceViolations.summary.approvedCount}
                  </Text>
                </Paper>
                <Paper withBorder p="sm" radius="md">
                  <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                    Rejected
                  </Text>
                  <Text fw={700} fz="xl" c="red">
                    {violations.geoFenceViolations.summary.rejectedCount}
                  </Text>
                </Paper>
              </SimpleGrid>
            )}

            <DataTable<GeoFenceViolation>
              columns={violationColumns}
              data={(violations?.geoFenceViolations?.data as GeoFenceViolation[]) ?? []}
              loading={violationsLoading}
              pagination={
                violations
                  ? {
                      page: violationsPage,
                      pageSize: 15,
                      total: violations.geoFenceViolations.total,
                      onPageChange: setViolationsPage,
                    }
                  : undefined
              }
              getRowKey={(row) => row.id}
              emptyMessage="No violations recorded for this geofence."
              actions={(row) =>
                row.status === 'PENDING_REVIEW' ? (
                  <Group gap="xs" wrap="nowrap">
                    <ActionIcon
                      variant="subtle"
                      color="green"
                      onClick={() => handleOpenReview(row, 'APPROVED')}
                      title="Approve violation"
                    >
                      <IconCheck size={16} />
                    </ActionIcon>
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      onClick={() => handleOpenReview(row, 'REJECTED')}
                      title="Reject violation"
                    >
                      <IconX size={16} />
                    </ActionIcon>
                  </Group>
                ) : (
                  <Text size="xs" c="dimmed">
                    {row.reviewedBy ? `By ${row.reviewedBy}` : '--'}
                  </Text>
                )
              }
            />
          </Stack>
        </Tabs.Panel>
      </Tabs>

      {/* Review Modal */}
      <Modal
        opened={reviewModalOpen}
        onClose={() => setReviewModalOpen(false)}
        title={
          reviewAction === 'APPROVED'
            ? 'Approve Violation'
            : 'Reject Violation'
        }
        centered
        size="sm"
      >
        <Stack gap="md">
          {reviewTarget && (
            <>
              <Paper withBorder p="sm" radius="md" bg="gray.0">
                <Stack gap={4}>
                  <Group justify="space-between">
                    <Text size="sm" fw={500}>
                      Employee
                    </Text>
                    <Text size="sm">{reviewTarget.employeeName}</Text>
                  </Group>
                  <Group justify="space-between">
                    <Text size="sm" fw={500}>
                      Action
                    </Text>
                    <Text size="sm">{reviewTarget.action.replace(/_/g, ' ')}</Text>
                  </Group>
                  <Group justify="space-between">
                    <Text size="sm" fw={500}>
                      Distance
                    </Text>
                    <Text size="sm" c="red" fw={500}>
                      {reviewTarget.distanceFromFence >= 1000
                        ? `${(reviewTarget.distanceFromFence / 1000).toFixed(1)} km`
                        : `${Math.round(reviewTarget.distanceFromFence)} m`}{' '}
                      away
                    </Text>
                  </Group>
                  <Group justify="space-between">
                    <Text size="sm" fw={500}>
                      Time
                    </Text>
                    <Text size="sm">
                      {new Date(reviewTarget.timestamp).toLocaleString()}
                    </Text>
                  </Group>
                </Stack>
              </Paper>

              <Text size="sm" c="dimmed">
                {reviewAction === 'APPROVED'
                  ? 'Approving this violation marks it as legitimate. The employee\'s punch will be accepted.'
                  : 'Rejecting this violation flags it for further action. The punch may need to be corrected.'}
              </Text>
            </>
          )}

          <Textarea
            label="Review Note (optional)"
            placeholder="Add context for this decision..."
            minRows={3}
            value={reviewNote}
            onChange={(e) => setReviewNote(e.currentTarget.value)}
          />

          <Group justify="flex-end" gap="sm">
            <Button
              variant="default"
              onClick={() => setReviewModalOpen(false)}
              disabled={reviewMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              color={reviewAction === 'APPROVED' ? 'green' : 'red'}
              onClick={handleConfirmReview}
              loading={reviewMutation.isPending}
              icon={
                reviewAction === 'APPROVED' ? (
                  <IconCheck size={16} />
                ) : (
                  <IconX size={16} />
                )
              }
            >
              {reviewAction === 'APPROVED' ? 'Approve' : 'Reject'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
